import {cloneDeep, isEqual, isNil, omitBy, pick} from 'lodash';
import {Address} from '../../../addresses/models/address.model';
import {LatLng} from '../../../addresses/models/lat-lng.model';
import {LocationType} from '../../../commons/types/location.type';
import {ServicePointModel} from '../../../service-points/angular/models/service-point.model';
import {
  getCompactAddressDisplay,
  getServicePointDisplayText
} from '../../../service-points/angular/utils/service-point-display.util';

export const UI_SERVICE_POINT_TYPE = 'SERVICE_POINT';
export const UI_CUSTOM_ADDRESS_TYPE = 'CUSTOM_ADDRESS';

const COORDINATE_TOLERANCE = 0.000001;
const ADDRESS_COMPARE_FIELDS: (keyof Address)[] = ['street', 'house', 'city', 'state', 'zipCode', 'country'];

export type AvailabilityLocationPoint = 'departure' | 'destination';

export type AvailabilityCustomAddresses = Partial<Record<AvailabilityLocationPoint, Address>>;

export interface AvailabilityLocationSettings {
  departurePointType?: string;
  destinationPointType?: string;
  departurePoint?: Address;
  destinationPoint?: Address;
  departureId?: string;
  destinationId?: string;
}

export interface AvailabilityLocationView {
  showSection: boolean;
  startLabel: string;
  endLabel: string;
  startHomeDisplay: string;
  endHomeDisplay: string;
  startServicePointPlace: string;
  endServicePointPlace: string;
}

export function hasLocationSettings(settings?: AvailabilityLocationSettings): boolean {
  if (!settings) {
    return false;
  }
  return !!(
    settings.departurePointType ||
    settings.destinationPointType ||
    settings.departurePoint ||
    settings.destinationPoint ||
    settings.departureId ||
    settings.destinationId
  );
}

export function buildDefaultLocationSettings(agentAddress?: Address): AvailabilityLocationSettings {
  const address = agentAddress ? cloneDeep(agentAddress) : undefined;
  return {
    departurePointType: LocationType.CUSTOM,
    departurePoint: address,
    departureId: undefined,
    destinationPointType: LocationType.CUSTOM,
    destinationPoint: address,
    destinationId: undefined
  };
}

// UI shows SERVICE_POINT / CUSTOM_ADDRESS; server stores CUSTOM + address (departureId kept for UI round-trip).
export function toServerLocationSettings(settings?: AvailabilityLocationSettings): AvailabilityLocationSettings | undefined {
  if (!settings) {
    return undefined;
  }
  const serverSettings = cloneDeep(settings);
  serverSettings.departurePointType = toServerPointType(serverSettings.departurePointType);
  serverSettings.destinationPointType = toServerPointType(serverSettings.destinationPointType);
  return serverSettings;
}

// Server has no separate type for a custom address: CUSTOM without id is Home Address
// while its address matches the agent address, otherwise it is a Custom Address.
export function normalizeLocationSettingsForUi(settings: AvailabilityLocationSettings, agentAddress?: Address): void {
  settings.departurePointType = toUiPointType(
    settings.departurePointType, settings.departureId, settings.departurePoint, agentAddress
  );
  settings.destinationPointType = toUiPointType(
    settings.destinationPointType, settings.destinationId, settings.destinationPoint, agentAddress
  );
  if (settings.departurePointType === LocationType.CUSTOM && !settings.departurePoint && agentAddress) {
    settings.departurePoint = cloneDeep(agentAddress);
  }
  if (settings.destinationPointType === LocationType.CUSTOM && !settings.destinationPoint && agentAddress) {
    settings.destinationPoint = cloneDeep(agentAddress);
  }
}

export function ensureLocationSettings(
  settings: AvailabilityLocationSettings | undefined,
  agentAddress?: Address
): AvailabilityLocationSettings {
  return settings || buildDefaultLocationSettings(agentAddress);
}

export function setLocationPointType(
  settings: AvailabilityLocationSettings,
  point: AvailabilityLocationPoint,
  type: string,
  agentAddress?: Address,
  customAddress?: Address
): void {
  let address: Address | undefined;
  if (type === LocationType.CUSTOM) {
    address = agentAddress;
  } else if (type === UI_CUSTOM_ADDRESS_TYPE) {
    address = customAddress;
  }

  if (point === 'departure') {
    settings.departurePointType = type;
    settings.departureId = undefined;
    settings.departurePoint = address ? cloneDeep(address) : undefined;
    return;
  }

  settings.destinationPointType = type;
  settings.destinationId = undefined;
  settings.destinationPoint = address ? cloneDeep(address) : undefined;
}

export function setLocationCustomAddress(
  settings: AvailabilityLocationSettings,
  point: AvailabilityLocationPoint,
  address?: Address
): void {
  setLocationPointType(settings, point, UI_CUSTOM_ADDRESS_TYPE, undefined, address);
}

// initialSettings are the saved settings normalized for UI: saved custom points that were not edited pass as is.
export function hasIncompleteCustomAddress(
  settings?: AvailabilityLocationSettings,
  initialSettings?: AvailabilityLocationSettings
): boolean {
  if (!settings) {
    return false;
  }
  return isIncompleteCustomPoint(
    settings.departurePointType, settings.departurePoint,
    initialSettings?.departurePointType, initialSettings?.departurePoint
  ) || isIncompleteCustomPoint(
    settings.destinationPointType, settings.destinationPoint,
    initialSettings?.destinationPointType, initialSettings?.destinationPoint
  );
}

export function clearLocationServicePoint(settings: AvailabilityLocationSettings, point: AvailabilityLocationPoint): void {
  if (point === 'departure') {
    settings.departureId = undefined;
    settings.departurePoint = undefined;
    return;
  }
  settings.destinationId = undefined;
  settings.destinationPoint = undefined;
}

export function setLocationServicePoint(
  settings: AvailabilityLocationSettings,
  point: AvailabilityLocationPoint,
  servicePoint: ServicePointModel
): void {
  const address = cloneDeep(servicePoint.address);
  if (servicePoint.externalId) {
    (address as Address & { externalId?: string }).externalId = servicePoint.externalId;
  }

  if (point === 'departure') {
    settings.departurePointType = UI_SERVICE_POINT_TYPE;
    settings.departureId = servicePoint.id;
    settings.departurePoint = address;
    return;
  }

  settings.destinationPointType = UI_SERVICE_POINT_TYPE;
  settings.destinationId = servicePoint.id;
  settings.destinationPoint = address;
}

export function buildAvailabilityLocationViews(
  availabilities: { locationSettings?: AvailabilityLocationSettings }[],
  agentAddress: Address | undefined,
  showSections: boolean[],
  selectedStartPlaces: string[],
  selectedEndPlaces: string[]
): AvailabilityLocationView[] {
  return availabilities.map((avail, index) => {
    const settings = avail.locationSettings;
    const startHomeDisplay = getCompactAddressDisplay(settings?.departurePoint || agentAddress);
    const endHomeDisplay = getCompactAddressDisplay(settings?.destinationPoint || agentAddress);

    let startServicePointPlace = '';
    if (settings?.departurePointType === UI_SERVICE_POINT_TYPE) {
      startServicePointPlace = selectedStartPlaces[index]
        || getServicePointDisplayText(settings.departurePoint);
    }

    let endServicePointPlace = '';
    if (settings?.destinationPointType === UI_SERVICE_POINT_TYPE) {
      endServicePointPlace = selectedEndPlaces[index]
        || getServicePointDisplayText(settings.destinationPoint);
    }

    const startCustomDisplay = settings?.departurePointType === UI_CUSTOM_ADDRESS_TYPE
      ? getCustomAddressDisplay(settings.departurePoint)
      : '';
    const endCustomDisplay = settings?.destinationPointType === UI_CUSTOM_ADDRESS_TYPE
      ? getCustomAddressDisplay(settings.destinationPoint)
      : '';

    return {
      showSection: !!showSections[index],
      startLabel: buildPointLabel('Start', settings?.departurePointType, startHomeDisplay, startServicePointPlace, startCustomDisplay),
      endLabel: buildPointLabel('End', settings?.destinationPointType, endHomeDisplay, endServicePointPlace, endCustomDisplay),
      startHomeDisplay,
      endHomeDisplay,
      startServicePointPlace,
      endServicePointPlace
    };
  });
}

function buildPointLabel(
  prefix: string,
  type: string | undefined,
  homeDisplay: string,
  servicePointPlace: string,
  customDisplay: string
): string {
  if (type === LocationType.CUSTOM) {
    return homeDisplay ? `${prefix}: ${homeDisplay}` : `${prefix}: Home Address`;
  }
  if (type === UI_SERVICE_POINT_TYPE) {
    return servicePointPlace ? `${prefix}: ${servicePointPlace}` : `${prefix}: Service Point`;
  }
  if (type === UI_CUSTOM_ADDRESS_TYPE) {
    return customDisplay ? `${prefix}: ${customDisplay}` : `${prefix}: Custom Address`;
  }
  return prefix;
}

function getCustomAddressDisplay(address?: Address): string {
  return address?.place || getCompactAddressDisplay(address);
}

function toServerPointType(type?: string): string | undefined {
  if (type === UI_SERVICE_POINT_TYPE || type === UI_CUSTOM_ADDRESS_TYPE || type === LocationType.DEPOT) {
    return LocationType.CUSTOM;
  }
  return type;
}

function toUiPointType(type: string | undefined, id: string | undefined, address: Address | undefined, agentAddress?: Address): string | undefined {
  if (type === LocationType.DEPOT) {
    return UI_SERVICE_POINT_TYPE;
  }
  if (type !== LocationType.CUSTOM) {
    return type;
  }
  if (id) {
    return UI_SERVICE_POINT_TYPE;
  }
  if (address && !isSameAddress(address, agentAddress)) {
    return UI_CUSTOM_ADDRESS_TYPE;
  }
  return type;
}

function isIncompleteCustomPoint(
  type: string | undefined,
  address: Address | undefined,
  initialType: string | undefined,
  initialAddress: Address | undefined
): boolean {
  if (type !== UI_CUSTOM_ADDRESS_TYPE || getAddressLatLng(address)) {
    return false;
  }
  return !(initialType === UI_CUSTOM_ADDRESS_TYPE && isEqual(address, initialAddress));
}

function isSameAddress(address: Address, otherAddress?: Address): boolean {
  if (!otherAddress) {
    return false;
  }
  const latLng = getAddressLatLng(address);
  const otherLatLng = getAddressLatLng(otherAddress);
  if (latLng && otherLatLng) {
    return Math.abs(latLng.latitude - otherLatLng.latitude) < COORDINATE_TOLERANCE
      && Math.abs(latLng.longitude - otherLatLng.longitude) < COORDINATE_TOLERANCE;
  }
  if (address.placeId && otherAddress.placeId) {
    return address.placeId === otherAddress.placeId;
  }
  if (address.place && otherAddress.place) {
    return address.place === otherAddress.place;
  }
  return isEqual(getAddressCompareFields(address), getAddressCompareFields(otherAddress));
}

function getAddressCompareFields(address: Address): Partial<Address> {
  return omitBy(pick(address, ADDRESS_COMPARE_FIELDS), isNil);
}

function getAddressLatLng(address?: Address): LatLng | undefined {
  const location = address?.location;
  if (isNil(location?.latitude) || isNil(location?.longitude)) {
    return undefined;
  }
  return location;
}
